import { useState, useEffect, useCallback } from 'react';
import { Sidebar } from '@/components/sidebar';
import { Dashboard } from '@/components/dashboard';
import { FeedbackManagement } from '@/components/feedback-management';
import { FeedbackDetail } from '@/components/feedback-detail-supabase';
import { IdeaManagement } from '@/components/idea-management';
import { IdeaDetail } from '@/components/idea-detail-supabase';
import { ProductDefinition } from '@/components/product-definition';
import { ClientManagement } from '@/components/client-management';
import { ClientDetail } from '@/components/client-detail-supabase';
import { Integrations } from '@/components/integrations';
import { SettingsNavigation } from '@/components/settings-navigation';
import { Auth } from '@/components/auth';
import { WorkspaceSelect } from '@/components/workspace-select';
import { AcceptInvitation } from '@/components/accept-invitation';
import { PasswordReset } from '@/components/password-reset';
import { MfaChallenge } from '@/components/mfa-challenge';
import { WorkspaceProvider, useWorkspace, getLastActiveWorkspace, useInvitations, updateLastActiveWorkspace } from '@/contexts/context';
import { useAuth } from '@/contexts/auth-context';
import { getViewForState, getNextState, type AuthState, type View } from '@/lib/auth-state-machine';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { supabase } from '@product-radar/shared';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useTrialStatus } from '@/hooks/use-trial-status';

// Main app content that requires workspace context
function AppContent({ onSwitchWorkspace, onSignOut }: { onSwitchWorkspace: () => void; onSignOut: () => void }) {
  const [activeView, setActiveView] = useState('dashboard');
  const [activeSettingsView, setActiveSettingsView] = useState('general');
  const [checkoutResult, setCheckoutResult] = useState<'success' | 'canceled' | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedFeedbackId, setSelectedFeedbackId] = useState<string | null>(null);
  const [selectedIdeaId, setSelectedIdeaId] = useState<string | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<any>(null);
  
  const { workspace, membership, isLoading, error } = useWorkspace();
  const { isTrialExpired, isTrialActive, daysRemaining, hasActiveSubscription, isLoading: isTrialLoading } = useTrialStatus();
  const isAdmin = membership?.role === 'admin';

  // Handle Stripe checkout redirect (success or cancellation) — capture params immediately
  // Handle Slack OAuth callback: ?code=...&state=workspace_id → switch to notifications
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'checkout') {
      setCheckoutResult('success');
      setActiveView('settings');
      setActiveSettingsView('billing');
      window.history.replaceState({}, '', window.location.pathname || '/');
    } else if (params.get('canceled') === 'true') {
      setCheckoutResult('canceled');
      setActiveView('settings');
      setActiveSettingsView('billing');
      window.history.replaceState({}, '', window.location.pathname || '/');
    } else if (params.get('code') && params.get('state')) {
      const path = window.location.pathname.replace(/\/$/, '') || '/';
      if (path === '/integrations') {
        setActiveView('integrations');
      } else {
        setActiveView('settings');
        setActiveSettingsView('notifications');
      }
    }
  }, []);

  // Sync URL path with view (for email links like /feedback, /ideas)
  useEffect(() => {
    if (isLoading) return;
    const path = window.location.pathname.replace(/\/$/, '') || '/';
    if (path === '/feedback') {
      setActiveView('feedback');
      const id = new URLSearchParams(window.location.search).get('id');
      if (id) setSelectedFeedbackId(id);
      else setSelectedFeedbackId(null);
    } else if (path === '/ideas') {
      setActiveView('ideas');
      const id = new URLSearchParams(window.location.search).get('id');
      if (id) setSelectedIdeaId(id);
      else setSelectedIdeaId(null);
    } else if (path === '/clients' || path === '/product-definition' || path === '/integrations') {
      setActiveView(path.slice(1));
    } else if (path === '/settings') {
      setActiveView('settings');
    }
  }, [isLoading]);

  // Update URL when view changes (for shareable links from email)
  useEffect(() => {
    if (isLoading || !workspace) return;
    let path = '/';
    if (activeView === 'feedback') path = selectedFeedbackId ? `/feedback?id=${selectedFeedbackId}` : '/feedback';
    else if (activeView === 'ideas') path = selectedIdeaId ? `/ideas?id=${selectedIdeaId}` : '/ideas';
    else if (activeView === 'clients') path = selectedClientId ? `/clients?id=${selectedClientId}` : '/clients';
    else if (['product-definition', 'integrations', 'settings'].includes(activeView)) path = `/${activeView}`;
    const search = activeView === 'settings' ? window.location.search : '';
    const desired = path + search;
    if (window.location.pathname + window.location.search !== desired) {
      window.history.replaceState({}, '', desired);
    }
  }, [isLoading, workspace, activeView, selectedFeedbackId, selectedIdeaId, selectedClientId]);

  useEffect(() => {
    // Load user profile when workspace is loaded
    if (workspace && membership) {
      loadUserProfile(membership.user_id);
    }
  }, [workspace, membership]);

  const loadUserProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    
    if (!error && data) {
      setUserProfile({
        ...data,
        role: membership?.role || 'viewer'
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#F2F0EF]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#245F73] mx-auto mb-4" />
          <p className="text-muted-foreground">Loading workspace...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#F2F0EF] p-6">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
              onClick={() => window.location.reload()}
            className="px-4 py-2 bg-[#245F73] text-white rounded"
            >
              Return to Workspace Selection
          </button>
        </div>
      </div>
    );
  }

  // Trial expired paywall -- blocks all workspace content except billing settings for admins
  const isOnBillingPage = activeView === 'settings' && activeSettingsView === 'billing';
  if (!isTrialLoading && isTrialExpired && !(isAdmin && isOnBillingPage)) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#F2F0EF]">
        <div className="max-w-md text-center p-8">
          <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-6">
            <AlertTriangle className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Free Trial Expired</h1>
          <p className="text-muted-foreground mb-6">
            Your 30-day free trial has ended. Subscribe to a plan to continue using Product Radar.
          </p>
          {isAdmin ? (
            <button
              onClick={() => {
                setActiveView('settings');
                setActiveSettingsView('billing');
              }}
              className="w-full px-4 py-2 bg-[#245F73] text-white rounded-md hover:bg-[#1d4f61] transition-colors mb-3"
            >
              Subscribe Now
            </button>
          ) : (
            <p className="text-sm text-muted-foreground bg-muted p-3 rounded-md mb-3">
              Contact your workspace admin to subscribe to a plan.
            </p>
          )}
          <div className="flex gap-3 justify-center">
            <button
              onClick={onSwitchWorkspace}
              className="px-4 py-2 text-sm border rounded-md hover:bg-muted transition-colors"
            >
              Switch Workspace
            </button>
            <button
              onClick={onSignOut}
              className="px-4 py-2 text-sm border rounded-md hover:bg-muted transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
        <Toaster />
      </div>
    );
  }

  const renderView = () => {
    switch (activeView) {
      case 'dashboard':
        return <Dashboard 
          onNavigateToIdea={(ideaId) => {
            setSelectedIdeaId(ideaId);
            setActiveView('ideas');
          }}
          onNavigateToFeedback={(feedbackId) => {
            setSelectedFeedbackId(feedbackId);
            setActiveView('feedback');
          }}
        />;
      case 'feedback':
        if (selectedFeedbackId) {
          return (
            <FeedbackDetail 
              feedbackId={selectedFeedbackId} 
              onBack={() => setSelectedFeedbackId(null)}
              onNavigateToIdea={(ideaId) => {
                setSelectedIdeaId(ideaId);
                setActiveView('ideas');
                setSelectedFeedbackId(null);
              }}
              onNavigateToClient={(clientId) => {
                setSelectedClientId(clientId);
                setActiveView('clients');
                setSelectedFeedbackId(null);
              }}
            />
          );
        }
        return <FeedbackManagement onViewDetails={setSelectedFeedbackId} />;
      case 'ideas':
        if (selectedIdeaId) {
          return (
            <IdeaDetail 
              ideaId={selectedIdeaId} 
              onBack={() => setSelectedIdeaId(null)}
              onNavigateToFeedback={(feedbackId) => {
                setSelectedFeedbackId(feedbackId);
                setActiveView('feedback');
                setSelectedIdeaId(null);
              }}
              onNavigateToClient={(clientId) => {
                setSelectedClientId(clientId);
                setActiveView('clients');
                setSelectedIdeaId(null);
              }}
            />
          );
        }
        return <IdeaManagement onViewDetails={setSelectedIdeaId} />;
      case 'product-definition':
        return <ProductDefinition />;
      case 'clients':
        if (selectedClientId) {
          return (
            <ClientDetail 
              clientId={selectedClientId} 
              onBack={() => setSelectedClientId(null)}
              onNavigateToFeedback={(feedbackId) => {
                setSelectedFeedbackId(feedbackId);
                setActiveView('feedback');
                setSelectedClientId(null);
              }}
              onNavigateToIdea={(ideaId) => {
                setSelectedIdeaId(ideaId);
                setActiveView('ideas');
                setSelectedClientId(null);
              }}
            />
          );
        }
        return <ClientManagement onViewDetails={setSelectedClientId} />;
      case 'integrations':
        return <Integrations />;
      case 'settings':
        return (
          <SettingsNavigation
            activeSettingsView={activeSettingsView}
            setActiveSettingsView={setActiveSettingsView}
            userProfile={userProfile}
            checkoutResult={checkoutResult}
            onCheckoutResultClose={() => setCheckoutResult(null)}
          />
        );
      default:
        return <Dashboard />;
    }
  };

  return (
    <div className="flex h-screen bg-[#F2F0EF]">
      <Sidebar 
        activeView={activeView}
        onNavigate={(view) => {
          setActiveView(view);
          setSelectedFeedbackId(null);
          setSelectedIdeaId(null);
          setSelectedClientId(null);
        }}
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        userProfile={userProfile}
        onSignOut={onSignOut}
        onSwitchWorkspace={onSwitchWorkspace}
        workspaceName={workspace?.name}
      />
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-16'}`}>
        <main className={`flex-1 overflow-hidden min-w-0 ${activeView === 'settings' ? '' : 'p-4 overflow-y-auto'}`}>
          {renderView()}
        </main>
      </div>
      <Toaster />
    </div>
  );
}

// Main App component with authentication and workspace routing
function AppInner() {
  const { session, user, authState, setAuthState, signOut } = useAuth();
  const { pendingInvitations, refreshInvitations } = useInvitations();
  const [currentView, setCurrentView] = useState<View | null>(null);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);
  const [showPasswordReset, setShowPasswordReset] = useState(false);

  // Check for password reset token in URL
  useEffect(() => {
      const hash = window.location.hash;
      const hashParams = new URLSearchParams(hash.substring(1));
    const type = hashParams.get('type');
    
    if (type === 'recovery') {
      setShowPasswordReset(true);
    }
  }, []);
      
  // OAuth callback is handled by AuthProvider

  // Route based on auth state
  useEffect(() => {
    const route = async () => {
      console.log('[route] start', { authState, selectedWorkspaceId, currentView, hasUser: !!user });
      
      // Guard: Don't re-run if we're already in the correct state
      if (authState === 'authenticated' && currentView === 'workspace' && selectedWorkspaceId) {
        console.log('[route] already authenticated with workspace, skipping');
        return;
      }
      
      if (authState === 'mfa_challenge') {
        setCurrentView('mfa_challenge');
        return;
      }
      
      switch (authState) {
        case 'initializing':
          // Still checking session, show loading
          console.log('[route] initializing - setting view to null');
          setCurrentView(null);
          break;

        case 'unauthenticated':
          console.log('[route] unauthenticated - setting view to landing');
          setCurrentView('landing'); // landing view = sign-in screen
          setSelectedWorkspaceId(null);
          break;

        case 'authenticating':
          // OAuth callback in progress, show loading
          console.log('[route] authenticating - setting view to null');
          setCurrentView(null);
          break;

        case 'checking_invitations':
          // Guard: Don't re-check if we're already authenticated
          if (currentView === 'workspace' && selectedWorkspaceId) {
            console.log('[route] checking_invitations - already authenticated, skipping');
            setAuthState('authenticated');
            break;
        }
        
          // Check for pending invitations
          console.log('[route] checking_invitations - fetching invitations');
          try {
            const invitations = await refreshInvitations();
            const hasInvitations = invitations.length > 0;
            console.log('[route] checking_invitations - hasInvitations:', hasInvitations);

            if (hasInvitations) {
              setAuthState('accepting_invitation');
          } else {
              setAuthState('selecting_workspace');
            }
          } catch (error) {
            console.error('[route] Error checking invitations:', error);
            setAuthState('selecting_workspace');
          }
          break;

        case 'accepting_invitation':
          console.log('[route] accepting_invitation - setting view to supabase-invitation');
          setCurrentView('supabase-invitation');
          break;

        case 'selecting_workspace':
          // Guard: Don't re-run if we already have a workspace selected and are authenticated
          if (selectedWorkspaceId && currentView === 'workspace') {
            console.log('[route] selecting_workspace - already have workspace, transitioning to authenticated');
            setAuthState('authenticated');
            break;
          }

          // Only auto-load last workspace if we're not already showing the selector
          // (i.e., user explicitly clicked "Switch Workspace")
          if (currentView === 'workspace-select') {
            console.log('[route] selecting_workspace - already showing selector, skipping auto-load');
            break;
          }
          
          // Check for last active workspace (only on initial transition to this state)
          console.log('[route] selecting_workspace - checking for last active workspace');
          try {
            if (user) {
            const lastWorkspaceId = await getLastActiveWorkspace();
              console.log('[route] selecting_workspace - lastWorkspaceId:', lastWorkspaceId);
            if (lastWorkspaceId) {
                console.log('[route] selecting_workspace - found workspace, setting selectedWorkspaceId and transitioning to loading_workspace');
              setSelectedWorkspaceId(lastWorkspaceId);
                setAuthState('loading_workspace');
              } else {
                console.log('[route] selecting_workspace - no last workspace, showing selector');
                setCurrentView('workspace-select');
              }
            } else {
              console.log('[route] selecting_workspace - no user, showing selector');
              setCurrentView('workspace-select');
            }
          } catch (error) {
            console.error('[route] Error getting last workspace:', error);
            setCurrentView('workspace-select');
          }
          break;

        case 'loading_workspace':
          console.log('[route] loading_workspace - selectedWorkspaceId:', selectedWorkspaceId);
          if (selectedWorkspaceId) {
            console.log('[route] loading_workspace - setting view to workspace and transitioning to authenticated');
            setCurrentView('workspace');
            setAuthState('authenticated');
          } else {
            console.log('[route] loading_workspace - no selectedWorkspaceId, transitioning to selecting_workspace');
            setAuthState('selecting_workspace');
          }
          break;

        case 'authenticated':
          console.log('[route] authenticated - selectedWorkspaceId:', selectedWorkspaceId);
          if (selectedWorkspaceId) {
            if (currentView !== 'workspace') {
              console.log('[route] authenticated - setting view to workspace');
              setCurrentView('workspace');
            } else {
              console.log('[route] authenticated - view already set to workspace');
            }
          } else {
            console.log('[route] authenticated - no selectedWorkspaceId, showing selector');
            setCurrentView('workspace-select');
          }
          break;
      }
      
      console.log('[route] end');
    };

    route();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState, user, selectedWorkspaceId]);

  // Handle invitation acceptance
  const handleInvitationAccepted = useCallback(async () => {
    // Refresh invitations
    await refreshInvitations();
    
    // Get last active workspace or show selector
    if (user) {
      try {
        const lastWorkspaceId = await getLastActiveWorkspace();
        if (lastWorkspaceId) {
          setSelectedWorkspaceId(lastWorkspaceId);
          setAuthState('loading_workspace');
        } else {
          setAuthState('selecting_workspace');
        }
      } catch (error) {
        console.error('Error getting workspace after invitation:', error);
        setAuthState('selecting_workspace');
      }
    } else {
      setAuthState('selecting_workspace');
    }
  }, [user, refreshInvitations, setAuthState]);

  // Handle workspace selection
  const handleWorkspaceSelected = useCallback(async (workspaceId: string) => {
    if (user) {
      try {
        await updateLastActiveWorkspace(workspaceId);
        setSelectedWorkspaceId(workspaceId);
        setAuthState('loading_workspace');
      } catch (error) {
        console.error('Error updating last workspace:', error);
        toast.error('Failed to switch workspace');
            }
    }
  }, [user]);

  // Handle sign out
  const handleSignOut = useCallback(async () => {
    await signOut();
    setSelectedWorkspaceId(null);
            setCurrentView('landing'); // landing view = sign-in screen
  }, [signOut]);

  // Render based on current view
  if (currentView === null && authState !== 'unauthenticated') {
    // Show loading
      return (
        <div className="flex items-center justify-center h-screen bg-[#F2F0EF]">
          <Loader2 className="h-8 w-8 animate-spin text-[#245F73]" />
        </div>
      );
    }

  if (currentView === 'mfa_challenge') {
    return (
      <>
        <MfaChallenge onVerified={() => {}} />
        <Toaster />
      </>
    );
  }

  if (currentView === 'supabase-invitation') {
    return (
      <>
        <AcceptInvitation
          onAccepted={handleInvitationAccepted}
          onError={() => {
            setAuthState('selecting_workspace');
          }}
        />
        <Toaster />
      </>
    );
  }

  if (currentView === 'workspace-select') {
    return (
      <>
        <WorkspaceSelect 
          onSelectWorkspace={handleWorkspaceSelected}
          onSignOut={handleSignOut}
        />
        <Toaster />
      </>
    );
  }
  
  if (currentView === 'workspace' && selectedWorkspaceId) {
    return (
      <WorkspaceProvider workspaceId={selectedWorkspaceId}>
        <AppContent 
          onSwitchWorkspace={() => {
            // Set view first, then state, to prevent auto-loading last workspace
            setCurrentView('workspace-select');
            setSelectedWorkspaceId(null);
            setAuthState('selecting_workspace');
          }}
          onSignOut={handleSignOut}
        />
      </WorkspaceProvider>
    );
  }
  
  // Show password reset if needed
  if (showPasswordReset) {
    return (
      <>
        <PasswordReset
          onSuccess={() => {
            setShowPasswordReset(false);
            // After password reset, user should sign in
            setAuthState('unauthenticated');
          }}
          onCancel={() => {
            setShowPasswordReset(false);
            setAuthState('unauthenticated');
          }}
        />
        <Toaster />
      </>
    );
  }

  // Unauthenticated: show sign-in screen (app root is sign-in; landing is a separate site)
  return (
    <>
      <Auth onAuthenticated={() => {}} />
      <Toaster />
    </>
  );
}

// Main App component
export default function App() {
  return <AppInner />;
}
